<template>
  <div class="container-fluid">
    <!-- Bread-crumb-->
    <div class="block-header">
      <bread-crumb></bread-crumb>
    </div>
    <div class="row clearfix">
      <div class="col-lg-12">
        <div class="card card-top-line">
          <!-- Header -->
          <div class="header d-flex justify-content-between align-items-center">
            <h2>FX Rates Management</h2>
          </div>
          <!-- Main Content -->
          <div class="body pt-0">
            <!-- Module Navigation -->
            <div
              class="d-flex justify-content-between align-items-center p-3 border-bottom"
            >
              <h4 class="mb-0">FX Rates Management</h4>
              <div class="btn-group" role="group">
                <template v-if="$store.getters['authpermission/hasAnyPermission'](['fxrates.update'])">
                  <router-link
                    to="/fx-rates/quick-update"
                    class="btn btn-outline-primary"
                  >
                    <i class="fa fa-bolt mr-2"></i>
                    Quick Rate Update
                  </router-link>
                </template>
                <template v-if="$store.getters['authpermission/hasAnyPermission'](['fxrates.csvupload'])">
                  <router-link
                    to="/fx-rates/csv-upload"
                    class="btn btn-outline-primary"
                  >
                    <i class="fa fa-upload mr-2"></i>
                    CSV File Upload
                  </router-link>
                </template>
              </div>
            </div>

            <!-- System Rates Content -->
            <div class="p-4">
              <h4 class="mb-4">System Rates</h4>

              <!-- Filters -->
              <div class="chart-top-action d-lg-flex justify-content-end py-3">
                <div class="mr-0 mr-lg-3 action-top-search">
                  <div class="input-group">
                    <input
                      type="text"
                      class="form-control"
                      v-model="systemRatesSearch.keywords"
                      placeholder="Search by currency code (USD, EUR, etc.)..."
                      aria-label="Search rates"
                      @keyup.enter="fetchSystemRates"
                      @input="onKeywordChange"
                    />
                    <div class="input-group-append">
                      <span class="input-group-text">
                        <i class="icon-magnifier"></i>
                      </span>
                    </div>
                  </div>
                  <!-- Currency search indicator -->
                  <small
                    v-if="
                      systemRatesSearch.keywords &&
                      systemRatesSearch.keywords.trim()
                    "
                    class="text-info mt-1 d-block"
                  >
                    <i class="fa fa-info-circle mr-1"></i>
                    Searching for rates involving
                    {{ systemRatesSearch.keywords.toUpperCase() }} currency
                  </small>
                </div>
                <div class="fliter-main mr-auto mt-3 mt-lg-0">
                  <b-button
                    class="fliter-btn btn-lg btn-mw"
                    :variant="isVisibleFilter ? 'primary' : 'outline-primary'"
                    @click="isVisibleFilter = !isVisibleFilter"
                  >
                    Filter
                    <i class="fa fa-filter" aria-hidden="true"></i>
                  </b-button>
                </div>
              </div>

              <!-- Filter Collapse -->
              <b-collapse
                id="fliter-collapse"
                class="fliter-collapse"
                v-model="isVisibleFilter"
              >
                <div class="d-flex justify-content-between p-3">
                  <div class="form-group">
                    <select
                      class="form-control"
                      v-model="systemRatesSearch.sourceCurrency"
                      :disabled="isLoadingCurrencies"
                    >
                      <option value="">All Source Currencies</option>
                      <template v-if="isLoadingCurrencies">
                        <option value="" disabled>Loading currencies...</option>
                      </template>
                      <template v-else>
                        <option
                          v-for="currency in sourceCurrencies"
                          :key="currency.currencyCode"
                          :value="currency.currencyCode"
                        >
                          {{ currency.currencyCode }} -
                          {{ currency.countryName }}
                        </option>
                      </template>
                    </select>
                  </div>
                  <div class="form-group">
                    <select
                      class="form-control"
                      v-model="systemRatesSearch.targetCurrency"
                      :disabled="isLoadingCurrencies"
                    >
                      <option value="">All Target Currencies</option>
                      <template v-if="isLoadingCurrencies">
                        <option value="" disabled>Loading currencies...</option>
                      </template>
                      <template v-else>
                        <option
                          v-for="currency in destinationCurrencies"
                          :key="currency.currencyCode"
                          :value="currency.currencyCode"
                        >
                          {{ currency.currencyCode }} -
                          {{ currency.countryName }}
                        </option>
                      </template>
                    </select>
                  </div>

                  <div class="form-group">
                    <button class="btn btn-primary h-100" @click="applyFilters">
                      Apply
                    </button>
                  </div>
                  <div class="form-group">
                    <button
                      class="btn btn-outline-danger h-100"
                      @click="clearSystemRatesFilter"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </b-collapse>

              <!-- Rates Table -->
              <b-table
                responsive
                table-class="mb-0 table-custom border"
                head-variant="light"
                :items="systemRates"
                :fields="systemRatesFields"
                :current-page="currentPage"
                :per-page="perPage"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :sort-direction="sortDirection"
                :busy="isLoading"
                show-empty
                empty-text="No FX rates found"
              >
                <template #table-busy>
                  <div class="text-center text-primary my-2">
                    <b-spinner class="align-middle"></b-spinner>
                    <strong>Loading...</strong>
                  </div>
                </template>

                <template #head()="{ label, field: { key } }">
                  {{ label }}
                  <template>
                    <i v-if="sortBy !== key" class=""></i>
                    <i
                      v-else-if="sortDesc"
                      class="fa fa-long-arrow-down ml-1"
                    ></i>
                    <i v-else class="fa fa-long-arrow-up ml-1"></i>
                  </template>
                </template>

                <template #cell(sourceCurrency)="data">
                  <span>{{ data.value }}</span>
                  <small v-if="data.item.sourceCountry" class="text-muted d-block">
                    {{ data.item.sourceCountry }}
                  </small>
                </template>

                <template #cell(targetCurrency)="data">
                  <span>{{ data.value }}</span>
                  <small v-if="data.item.targetCountry" class="text-muted d-block">
                    {{ data.item.targetCountry }}
                  </small>
                </template>

                <template #cell(rate)="data">
                  {{ formatRate(data.value) }}
                </template>

                <template #cell(status)="data">
                  <span class="badge" :class="getStatusBadgeClass(data.value)">
                    {{ getStatusDisplayName(data.value) }}
                  </span>
                </template>

                <template #cell(oldRate)="data">
                  <span v-if="data.value" :class="getRateChangeClass(data.item.rate, data.value)">
                    {{ formatRate(data.value) }}
                    <small class="ml-1" :class="getRateChangeClass(data.item.rate, data.value)">
                      ({{ getPercentageChange(data.item.rate, data.value) }})
                    </small>
                  </span>
                  <span v-else class="text-muted font-italic">N/A</span>
                </template>

                <template #cell(effectiveDate)="data">
                  {{ formatDate(data.value) }}
                </template>

                <template #cell(actions)="data">
                  <b-dropdown size="sm" variant="outline-primary" right>
                    <template #button-content>
                      <i class="fa fa-cog"></i>
                      Actions
                    </template>
                    <template v-if="$store.getters['authpermission/hasAnyPermission'](['fxrates.edit'])">
                      <b-dropdown-item @click="editRate(data.item)">
                        <i class="fa fa-edit mr-2"></i>
                        Edit Rate
                      </b-dropdown-item>
                    </template>
                    <template v-if="$store.getters['authpermission/hasAnyPermission'](['fxrates.status'])">
                      <b-dropdown-item
                        v-if="isActive(data.item.status)"
                        @click="deactivateRate(data.item)"
                      >
                        <i class="fa fa-ban mr-2"></i>
                        Make Inactive
                      </b-dropdown-item>
                      <b-dropdown-item
                        v-if="isInactive(data.item.status)"
                        @click="activateRate(data.item)"
                      >
                        <i class="fa fa-check mr-2"></i>
                        Make Active
                      </b-dropdown-item>
                    </template>
                    <b-dropdown-divider></b-dropdown-divider>
                    <b-dropdown-item @click="viewRateHistory(data.item)">
                      <i class="fa fa-line-chart mr-2"></i>
                      View History
                    </b-dropdown-item>
                  </b-dropdown>
                </template>
              </b-table>

              <!-- Table Pagination -->
              <div
                class="py-3 d-flex align-items-center justify-content-between table-pagination"
              >
                <div class="per-page">
                  <b-form-group
                    label="Per page"
                    label-for="per-page-select"
                    class="mb-0"
                  >
                    <b-form-select
                      id="per-page-select"
                      v-model="perPage"
                      :options="pageOptions"
                      size="sm"
                      @change="onPerPageChange"
                    ></b-form-select>
                  </b-form-group>
                </div>
                <div class="pagination-info">
                  <span class="text-muted">
                    Showing {{ startRecord }} to {{ endRecord }} of
                    {{ totalRows }} entries
                  </span>
                </div>
                <div class="number-page">
                  <b-pagination
                    v-model="currentPage"
                    :total-rows="totalRows"
                    :per-page="perPage"
                    hide-goto-end-buttons
                    hide-ellipsis
                    prev-text="Previous page"
                    next-text="Next page"
                    align="right"
                    @change="onPageChange"
                  ></b-pagination>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Rate History Modal -->
    <b-modal
      v-model="historyModalVisible"
      :title="selectedRateForHistory ? `Rate History: ${selectedRateForHistory.sourceCurrency} → ${selectedRateForHistory.targetCurrency}` : 'Rate History'"
      size="lg"
      hide-footer
      centered
      @shown="onHistoryModalShown"
      @hidden="onHistoryModalHidden"
    >
      <!-- Loading State -->
      <div v-if="historyChartLoading" class="text-center py-5">
        <b-spinner variant="primary" class="mb-3"></b-spinner>
        <p class="text-muted mb-0">Loading rate history...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="rateHistoryError" class="text-center py-5">
        <i class="fa fa-exclamation-circle text-danger fa-3x mb-3"></i>
        <p class="text-danger mb-0">{{ rateHistoryError }}</p>
      </div>

      <!-- Preparing Chart State -->
      <div v-else-if="pendingHistoryData && !rateHistoryChartOptions" class="text-center py-5">
        <b-spinner variant="primary" class="mb-3"></b-spinner>
        <p class="text-muted mb-0">Preparing chart...</p>
      </div>

      <!-- Chart -->
      <div v-else-if="rateHistoryChartOptions" class="rate-history-chart-container">
        <apexchart
          type="area"
          height="350"
          :options="rateHistoryChartOptions"
          :series="rateHistorySeries"
        ></apexchart>
      </div>

      <!-- Empty State -->
      <div v-else class="text-center py-5">
        <i class="fa fa-line-chart text-muted fa-3x mb-3"></i>
        <p class="text-muted mb-0">No historical data available</p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import "@/plugins/sweetalert2";
import "@/plugins/vee-validate";
import VueApexCharts from "vue-apexcharts";
import { ArchBaseService } from "@/common/api.service.js";

export default {
  name: "FxRatesComponent",
  components: {
    BreadCrumb: () => import("@/components/BreadCrumb.vue"),
    apexchart: VueApexCharts,
  },
  data() {
    return {
      // System Rates - now starts empty and gets populated by API
      systemRates: [],

      systemRatesFields: [
        {
          key: "sourceCurrency",
          label: "Source Currency",
          sortable: true,
          thClass: "th_sortfix",
        },
        {
          key: "targetCurrency",
          label: "Target Currency",
          sortable: true,
          thClass: "th_sortfix",
        },
        {
          key: "rate",
          label: "Exchange Rate",
          sortable: true,
          thClass: "th_sortfix",
        },
        {
          key: "oldRate",
          label: "Old FX Rate",
          sortable: true,
          thClass: "th_sortfix",
        },
        {
          key: "status",
          label: "Status",
          sortable: true,
          thClass: "th_sortfix",
        },
        {
          key: "effectiveDate",
          label: "Effective Date",
          sortable: true,
          thClass: "th_sortfix",
        },
        {
          key: "actions",
          label: "Actions",
          sortable: false,
          thClass: "th_sortfix text-center",
        },
      ],

      systemRatesSearch: {
        keywords: "",
        sourceCurrency: "",
        targetCurrency: "",
      },

      // Pagination
      totalRows: 0,
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 25, 50, 100],
      sortBy: "",
      sortDesc: false,
      sortDirection: "asc",

      // Loading and Filter states
      isLoading: false,
      isVisibleFilter: false,
      isLoadingCurrencies: false,

      // API error handling
      apiError: null,

      // Currency arrays for dropdowns - will be populated from API
      sourceCurrencies: [],
      destinationCurrencies: [],

      // Rate History Modal state
      historyModalVisible: false,
      historyChartLoading: false,
      selectedRateForHistory: null,
      rateHistoryChartOptions: null,
      rateHistorySeries: [],
      rateHistoryError: null,
      pendingHistoryData: null,
      historyModalReady: false,
    };
  },
  computed: {
    startRecord() {
      return (this.currentPage - 1) * this.perPage + 1;
    },
    endRecord() {
      return Math.min(this.currentPage * this.perPage, this.totalRows);
    },

    // Check if current keyword is a currency code
    isKeywordCurrency() {
      if (!this.systemRatesSearch.keywords) return false;
      const keyword = this.systemRatesSearch.keywords.trim().toUpperCase();
      const allCurrencies = [
        ...this.sourceCurrencies,
        ...this.destinationCurrencies,
      ];
      const currencyCodes = allCurrencies.map(
        (currency) => currency.currencyCode,
      );
      const isCurrency = currencyCodes.includes(keyword);
      return isCurrency;
    },
  },
  methods: {
    // Fetch available currencies from API
    async fetchAvailableCurrencies() {
      this.isLoadingCurrencies = true;
      try {
        // Initialize and set headers for ArchBaseService
        ArchBaseService.init();
        ArchBaseService.setHeader();

        // Fetch source currencies
        const sourceResponse = await ArchBaseService.get(
          "fx-rates/source-currencies",
        );
        this.sourceCurrencies = sourceResponse.data;

        // Fetch destination currencies
        const destinationResponse = await ArchBaseService.get(
          "fx-rates/destination-countries",
        );
        this.destinationCurrencies = destinationResponse.data;
      } catch (error) {
        console.error("Error fetching currencies:", error);

        // Fallback to common currencies if API fails
        this.sourceCurrencies = [
          { currencyCode: "USD", countryName: "United States Dollar" },
          { currencyCode: "EUR", countryName: "Euro" },
          { currencyCode: "GBP", countryName: "British Pound Sterling" },
        ];
        this.destinationCurrencies = [
          { currencyCode: "GHS", countryName: "Ghanaian Cedi" },
          { currencyCode: "NGN", countryName: "Nigerian Naira" },
        ];

        // Show error to user
        this.$swal({
          title: "Warning!",
          text: "Failed to load currency list. Using default currencies.",
          icon: "warning",
          confirmButtonText: "OK",
        });
      } finally {
        this.isLoadingCurrencies = false;
      }
    },
    // Main API call to fetch system rates
    async fetchSystemRates() {
      this.isLoading = true;
      this.apiError = null;

      try {
        // Build query parameters
        const params = {
          page: this.currentPage,
          limit: this.perPage,
        };

        // Add filters if they exist
        if (this.systemRatesSearch.sourceCurrency) {
          params.source_currency = this.systemRatesSearch.sourceCurrency;
        }
        if (this.systemRatesSearch.targetCurrency) {
          params.target_currency = this.systemRatesSearch.targetCurrency;
        }

        // Handle keyword search - always use currency_search
        if (
          this.systemRatesSearch.keywords &&
          this.systemRatesSearch.keywords.trim()
        ) {
          const keyword = this.systemRatesSearch.keywords.trim().toUpperCase();

          // Always use currency_search parameter for keyword input
          // Remove individual currency filters to avoid conflicts
          delete params.source_currency;
          delete params.target_currency;

          // Add currency search that will look for this keyword on both sides
          params.currency_search = keyword;
        } else {
          // No specific search criteria, fetch all rates
        }

        // Initialize and set headers for ArchBaseService
        ArchBaseService.init();
        ArchBaseService.setHeader();

        // Make API call using ArchBaseService
        const response = await ArchBaseService.get("fx-rates/all", params);

        // Process the response
        if (response.data && response.data.data) {
          this.systemRates = response.data.data.map((rate) => ({
            rateId: rate.rateId,
            pairId: rate.pairId,
            sourceCurrency: rate.sourceCurrency,
            targetCurrency: rate.targetCurrency,
            sourceCountry: rate.sourceCountry,
            sourceCountryCode: rate.sourceCountryCode,
            targetCountry: rate.targetCountry,
            targetCountryCode: rate.targetCountryCode,
            rate: rate.rate,
            oldRate: rate.oldRate,
            effectiveDate: rate.effectiveDate,
            status: this.normalizeStatus(rate.status),
            sourceCurrencyId: rate.sourceCurrencyId,
            targetCurrencyId: rate.targetCurrencyId,
          }));

          // Update pagination info
          if (response.data.meta) {
            this.totalRows = response.data.meta.total;
            this.currentPage = response.data.meta.page;
          }
        } else {
          this.systemRates = [];
          this.totalRows = 0;
        }
      } catch (error) {
        this.apiError =
          error.response?.data?.message || "Failed to fetch FX rates";
        this.systemRates = [];
        this.totalRows = 0;

        // Show error to user
        this.$swal({
          title: "Error!",
          text: this.apiError,
          icon: "error",
          confirmButtonText: "OK",
        });
      } finally {
        this.isLoading = false;
      }
    },

    // Apply filters and refresh data
    async applyFilters() {
      this.currentPage = 1; // Reset to first page when filtering
      await this.fetchSystemRates();
      this.isVisibleFilter = false;
    },

    // Clear all filters
    async clearSystemRatesFilter() {
      this.systemRatesSearch = {
        keywords: "",
        sourceCurrency: "",
        targetCurrency: "",
      };
      this.currentPage = 1;
      await this.fetchSystemRates();
      this.isVisibleFilter = false;
    },

    // Refresh available currencies
    async refreshCurrencies() {
      await this.fetchAvailableCurrencies();
    },

    // Handle keyword search changes
    async onKeywordChange() {
      // If keywords are cleared, refresh data
      if (
        !this.systemRatesSearch.keywords ||
        !this.systemRatesSearch.keywords.trim()
      ) {
        this.currentPage = 1;
        await this.fetchSystemRates();
      } else {
        // Keywords are present, search will be triggered on next filter application
      }
    },

    // Pagination handlers
    async onPageChange(page) {
      this.currentPage = page;
      await this.fetchSystemRates();
    },

    async onPerPageChange() {
      this.currentPage = 1;
      await this.fetchSystemRates();
    },

    // Helper method to fix SweetAlert positioning
    fixSweetAlertPosition() {
      // Apply styles directly to existing elements
      const applyStyles = () => {
        const containers = document.querySelectorAll(".swal2-container");
        const popups = document.querySelectorAll(".swal2-popup");

        containers.forEach((container) => {
          container.style.setProperty("position", "fixed", "important");
          container.style.setProperty("top", "0", "important");
          container.style.setProperty("left", "0", "important");
          container.style.setProperty("right", "0", "important");
          container.style.setProperty("bottom", "0", "important");
          container.style.setProperty("width", "100%", "important");
          container.style.setProperty("height", "100%", "important");
          container.style.setProperty("display", "flex", "important");
          container.style.setProperty("align-items", "center", "important");
          container.style.setProperty("justify-content", "center", "important");
          container.style.setProperty("z-index", "999999", "important");
          container.style.setProperty(
            "background-color",
            "rgba(0, 0, 0, 0.4)",
            "important",
          );
        });

        popups.forEach((popup) => {
          popup.style.setProperty("position", "static", "important");
          popup.style.setProperty("top", "auto", "important");
          popup.style.setProperty("left", "auto", "important");
          popup.style.setProperty("right", "auto", "important");
          popup.style.setProperty("bottom", "auto", "important");
          popup.style.setProperty("transform", "none", "important");
          popup.style.setProperty("margin", "0", "important");
          popup.style.setProperty("border-radius", "8px", "important");
          popup.style.setProperty(
            "box-shadow",
            "0 4px 20px rgba(0, 0, 0, 0.15)",
            "important",
          );
          popup.style.setProperty("padding", "25px", "important");
          popup.style.setProperty("background", "white", "important");
          popup.style.setProperty("width", "500px", "important");
          popup.style.setProperty("max-width", "90vw", "important");
        });

        // Style the buttons
        const confirmButtons = document.querySelectorAll(".swal2-confirm");
        const cancelButtons = document.querySelectorAll(".swal2-cancel");
        const actions = document.querySelectorAll(".swal2-actions");
        const titles = document.querySelectorAll(".swal2-title");

        confirmButtons.forEach((btn) => {
          btn.style.setProperty("background-color", "#007bff", "important");
          btn.style.setProperty("border", "none", "important");
          btn.style.setProperty("padding", "10px 20px", "important");
          btn.style.setProperty("font-size", "14px", "important");
          btn.style.setProperty("font-weight", "500", "important");
          btn.style.setProperty("border-radius", "4px", "important");
          btn.style.setProperty("color", "white", "important");
          btn.style.setProperty("cursor", "pointer", "important");
          btn.style.setProperty("margin", "0 5px", "important");
        });

        cancelButtons.forEach((btn) => {
          btn.style.setProperty("background-color", "#6c757d", "important");
          btn.style.setProperty("border", "none", "important");
          btn.style.setProperty("padding", "10px 20px", "important");
          btn.style.setProperty("font-size", "14px", "important");
          btn.style.setProperty("font-weight", "500", "important");
          btn.style.setProperty("border-radius", "4px", "important");
          btn.style.setProperty("color", "white", "important");
          btn.style.setProperty("cursor", "pointer", "important");
          btn.style.setProperty("margin", "0 5px", "important");
        });

        actions.forEach((actionDiv) => {
          actionDiv.style.setProperty("margin-top", "25px", "important");
          actionDiv.style.setProperty("display", "flex", "important");
          actionDiv.style.setProperty("gap", "10px", "important");
          actionDiv.style.setProperty("justify-content", "center", "important");
        });

        titles.forEach((title) => {
          title.style.setProperty("font-size", "18px", "important");
          title.style.setProperty("font-weight", "600", "important");
          title.style.setProperty("color", "#333", "important");
          title.style.setProperty("margin-bottom", "20px", "important");
          title.style.setProperty("text-align", "center", "important");
        });
      };

      // Apply immediately and then observe for changes
      applyStyles();

      // Create observer to fix positioning when modals are created
      const observer = new MutationObserver((mutations) => {
        let shouldFix = false;
        mutations.forEach((mutation) => {
          if (mutation.addedNodes) {
            mutation.addedNodes.forEach((node) => {
              if (
                node.classList &&
                (node.classList.contains("swal2-container") ||
                  (node.querySelector &&
                    node.querySelector(".swal2-container")))
              ) {
                shouldFix = true;
              }
            });
          }
        });
        if (shouldFix) {
          setTimeout(applyStyles, 10);
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      // Disconnect observer after 10 seconds to avoid memory leaks
      setTimeout(() => observer.disconnect(), 10000);
    },

    // Action methods (these will need actual API endpoints later)
    async editRate(rate) {
      // Fix SweetAlert positioning
      this.fixSweetAlertPosition();

      // Create a form for editing the rate
      const { value: formValues } = await this.$swal({
        title: `Edit Rate: ${rate.sourceCurrency} to ${rate.targetCurrency}`,
        html: `
                    <div style="text-align: left; padding: 10px;">
                        <div style="margin-bottom: 20px;">
                            <label for="edit-rate" style="display: block; margin-bottom: 8px; font-weight: 500; color: #333;">
                                Exchange Rate <span style="color: #dc3545;">*</span>
                            </label>
                            <input id="edit-rate"
                                   type="text"
                                   value="${rate.rate}"
                                   placeholder="Enter exchange rate"
                                   style="width: 100%; padding: 10px; border: 1px solid #ced4da; border-radius: 4px; font-size: 14px; box-sizing: border-box;">
                            <small style="display: block; margin-top: 5px; color: #6c757d; font-size: 12px;">
                                Format: Positive number with up to 8 decimal places
                            </small>
                        </div>
                        <div style="margin-bottom: 10px;">
                            <label for="edit-comments" style="display: block; margin-bottom: 8px; font-weight: 500; color: #333;">
                                Comments (Optional)
                            </label>
                            <textarea id="edit-comments"
                                      placeholder="Add any comments about this rate update (max 2000 characters)"
                                      rows="3"
                                      style="width: 100%; padding: 10px; border: 1px solid #ced4da; border-radius: 4px; font-size: 14px; resize: vertical; box-sizing: border-box; font-family: inherit;"></textarea>
                        </div>
                    </div>
                `,
        showCancelButton: true,
        confirmButtonText: "Update Rate",
        cancelButtonText: "Cancel",
        focusConfirm: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        willClose: () => {
          // Clean up the injected CSS when modal closes
          const existingStyle = document.getElementById("swal2-custom-fix");
          if (existingStyle) {
            existingStyle.remove();
          }
        },
        preConfirm: () => {
          const newRate = document.getElementById("edit-rate").value;
          const comments = document.getElementById("edit-comments").value;

          // Validate rate format
          const rateRegex = /^(?:0|[1-9]\d*)(?:\.\d{1,8})?$/;
          if (!newRate || !rateRegex.test(newRate)) {
            this.$swal.showValidationMessage(
              "Rate must be a positive number with up to 8 decimal places",
            );
            return false;
          }

          // Validate comments length
          if (comments && comments.length > 2000) {
            this.$swal.showValidationMessage(
              "Comments cannot exceed 2000 characters",
            );
            return false;
          }

          return { rate: newRate, comments: comments };
        },
      });

      if (formValues) {
        try {
          // Show loading state
          this.fixSweetAlertPosition();
          this.$swal({
            title: "Updating Rate...",
            text: "Please wait while we update the exchange rate.",
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
              this.$swal.showLoading();
              this.fixSweetAlertPosition();
            },
          });

          // Initialize and set headers for ArchBaseService
          ArchBaseService.init();
          ArchBaseService.setHeader();

          // Look up country IDs from the fetched currency lists using country code
          const sourceCountry = this.sourceCurrencies.find(
            (c) => c.alpha2 === rate.sourceCountryCode,
          );
          const targetCountry = this.destinationCurrencies.find(
            (c) => c.alpha2 === rate.targetCountryCode,
          );

          // Prepare the request payload
          const payload = {
            source_currency: rate.sourceCurrency,
            target_currency: rate.targetCurrency,
            source_country_id: sourceCountry ? sourceCountry.countryId : undefined,
            target_country_id: targetCountry ? targetCountry.countryId : undefined,
            rate: formValues.rate,
          };

          // Only include comments if it has a value
          if (formValues.comments && formValues.comments.trim()) {
            payload.comments = formValues.comments.trim();
          }

          // Call the manual-update endpoint using ArchBaseService
          const response = await ArchBaseService.post(
            "fx-rates/manual-update",
            payload,
          );

          if (response.status === 201) {
            this.fixSweetAlertPosition();
            this.$swal({
              title: "Success!",
              text: "Exchange rate updated successfully",
              icon: "success",
              confirmButtonText: "OK",
            });

            // Refresh the rates data
            await this.fetchSystemRates();
          } else {
            throw new Error("Failed to update exchange rate");
          }
        } catch (error) {
          console.error("Error updating exchange rate:", error);

          let errorMessage =
            "Failed to update exchange rate. Please try again.";

          if (error.response && error.response.data) {
            if (error.response.data.message) {
              errorMessage = error.response.data.message;
            } else if (error.response.data.errors) {
              errorMessage = "Please correct the validation errors.";
            }
          }

          this.fixSweetAlertPosition();
          this.$swal({
            title: "Error!",
            text: errorMessage,
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    },

    async deactivateRate(rate) {
      this.fixSweetAlertPosition();
      const result = await this.$swal({
        title: "Deactivate Rate",
        text: `Are you sure you want to deactivate the rate for ${rate.sourceCurrency} to ${rate.targetCurrency}?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, deactivate",
        cancelButtonText: "Cancel",
      });

      if (result.isConfirmed) {
        try {
          // Show loading state
          this.fixSweetAlertPosition();
          this.$swal({
            title: "Deactivating Rate...",
            text: "Please wait while we deactivate the rate.",
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
              this.$swal.showLoading();
            },
          });

          // Initialize and set headers for ArchBaseService
          ArchBaseService.init();
          ArchBaseService.setHeader();

          // Call the deactivate endpoint using ArchBaseService
          const response = await ArchBaseService.post(
            `fx-rates/${rate.rateId}/disable`,
          );

          if (response.status === 200) {
            this.fixSweetAlertPosition();
            this.$swal({
              title: "Success!",
              text: "Rate deactivated successfully",
              icon: "success",
              confirmButtonText: "OK",
            });

            // Refresh the rates data
            await this.fetchSystemRates();
          } else {
            throw new Error("Failed to deactivate rate");
          }
        } catch (error) {
          console.error("Error deactivating rate:", error);

          let errorMessage = "Failed to deactivate rate. Please try again.";

          if (error.response && error.response.data) {
            if (error.response.data.message) {
              errorMessage = error.response.data.message;
            }
          }

          this.$swal({
            title: "Error!",
            text: errorMessage,
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    },

    async activateRate(rate) {
      this.fixSweetAlertPosition();
      const result = await this.$swal({
        title: "Activate Rate",
        text: `Are you sure you want to activate the rate for ${rate.sourceCurrency} to ${rate.targetCurrency}?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, activate",
        cancelButtonText: "Cancel",
      });

      if (result.isConfirmed) {
        try {
          // Show loading state
          this.fixSweetAlertPosition();
          this.$swal({
            title: "Activating Rate...",
            text: "Please wait while we activate the rate.",
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
              this.$swal.showLoading();
            },
          });

          // Initialize and set headers for ArchBaseService
          ArchBaseService.init();
          ArchBaseService.setHeader();

          // Call the activate endpoint using ArchBaseService
          const response = await ArchBaseService.post(
            `fx-rates/${rate.rateId}/enable`,
          );

          if (response.status === 200) {
            this.fixSweetAlertPosition();
            this.$swal({
              title: "Success!",
              text: "Rate activated successfully",
              icon: "success",
              confirmButtonText: "OK",
            });

            // Refresh the rates data
            await this.fetchSystemRates();
          } else {
            throw new Error("Failed to activate rate");
          }
        } catch (error) {
          console.error("Error activating rate:", error);

          let errorMessage = "Failed to activate rate. Please try again.";

          if (error.response && error.response.data) {
            if (error.response.data.message) {
              errorMessage = error.response.data.message;
            }
          }

          this.$swal({
            title: "Error!",
            text: errorMessage,
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    },

    async viewRateHistory(rate) {
      this.selectedRateForHistory = rate;
      this.historyModalVisible = true;
      this.historyChartLoading = true;
      this.rateHistoryError = null;
      this.rateHistoryChartOptions = null;
      this.pendingHistoryData = null;
      this.historyModalReady = false;

      try {
        // Initialize and set headers for ArchBaseService
        ArchBaseService.init();
        ArchBaseService.setHeader();

        // Fetch rate history from API
        const response = await ArchBaseService.get(
          `fx-rates/${rate.pairId}/history`,
          { limit: 50 },
        );

        // Handle different response structures (data might be wrapped)
        const historyData = response.data?.data || response.data;

        console.log("Rate history response:", response.data);
        console.log("Parsed history data:", historyData);

        // Check if we have data points
        if (!historyData || !historyData.dataPoints || historyData.dataPoints.length === 0) {
          this.rateHistoryError = "No historical data available for this rate.";
          return;
        }

        // Store the data
        this.pendingHistoryData = historyData;

        // If modal is already shown, build chart immediately
        if (this.historyModalReady) {
          this.buildHistoryChart();
        }

      } catch (error) {
        console.error("Error fetching rate history:", error);
        console.error("Error response:", error.response);
        this.rateHistoryError =
          error.response?.data?.message ||
          error.message ||
          "Failed to load rate history. Please try again.";
      } finally {
        this.historyChartLoading = false;
      }
    },

    // Called when history modal is fully shown
    onHistoryModalShown() {
      console.log("Modal shown, pendingHistoryData:", this.pendingHistoryData ? "EXISTS" : "NULL");
      this.historyModalReady = true;

      // If data is already loaded, build chart now
      if (this.pendingHistoryData && this.pendingHistoryData.dataPoints) {
        this.buildHistoryChart();
      }
    },

    // Build the history chart from pendingHistoryData (using ApexCharts)
    buildHistoryChart() {
      if (!this.pendingHistoryData || !this.pendingHistoryData.dataPoints) {
        console.log("No data to build chart");
        return;
      }

      console.log("Building chart with", this.pendingHistoryData.dataPoints.length, "data points");

      // Transform data for the chart
      const chartData = this.pendingHistoryData.dataPoints.map((point) => ({
        x: new Date(point.validFrom).getTime(),
        y: parseFloat(point.rate),
      }));

      // Sort by timestamp ascending
      chartData.sort((a, b) => a.x - b.x);

      // Build ApexCharts series
      this.rateHistorySeries = [
        {
          name: "Exchange Rate",
          data: chartData,
        },
      ];

      // Build ApexCharts options
      this.rateHistoryChartOptions = {
        chart: {
          type: "area",
          height: 350,
          zoom: {
            enabled: true,
          },
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true,
            },
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          curve: "stepline",
          width: 2,
        },
        colors: ["#2563eb"],
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.4,
            opacityTo: 0.1,
            stops: [0, 90, 100],
          },
        },
        xaxis: {
          type: "datetime",
          labels: {
            datetimeUTC: false,
            format: "dd MMM yyyy",
          },
        },
        yaxis: {
          labels: {
            formatter: (val) => val ? val.toFixed(4) : "",
          },
          title: {
            text: "Rate",
          },
        },
        tooltip: {
          x: {
            format: "dd MMM yyyy HH:mm",
          },
          y: {
            formatter: (val) => val ? val.toFixed(8) : "",
          },
        },
        markers: {
          size: 4,
          colors: ["#2563eb"],
          strokeColors: "#fff",
          strokeWidth: 2,
          hover: {
            size: 6,
          },
        },
        grid: {
          borderColor: "#e5e7eb",
          strokeDashArray: 4,
        },
      };

      console.log("ApexChart options set successfully");
    },

    // Reset modal state when hidden
    onHistoryModalHidden() {
      this.rateHistoryChartOptions = null;
      this.rateHistorySeries = [];
      this.rateHistoryError = null;
      this.selectedRateForHistory = null;
      this.pendingHistoryData = null;
      this.historyModalReady = false;
    },

    // Utility methods
    formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      return d.toLocaleDateString();
    },

    formatRate(rate) {
      if (rate === null || rate === undefined || rate === "") return "";
      return parseFloat(rate).toLocaleString(undefined, {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      });
    },

    // Rate change styling helper
    getRateChangeClass(currentRate, oldRate) {
      if (!oldRate) return "";
      const current = parseFloat(currentRate);
      const old = parseFloat(oldRate);
      if (current > old) return "text-success";
      if (current < old) return "text-danger";
      return "";
    },

    // Percentage change calculation helper
    getPercentageChange(currentRate, oldRate) {
      if (!oldRate) return "";
      const current = parseFloat(currentRate);
      const old = parseFloat(oldRate);
      const change = ((current - old) / old) * 100;
      const sign = change > 0 ? "+" : "";
      return `${sign}${change.toFixed(2)}%`;
    },

    // Status helper methods
    normalizeStatus(status) {
      if (status === null || status === undefined) return "inactive";
      if (typeof status === "boolean") return status ? "active" : "inactive";
      const value = String(status).toLowerCase().trim();

      // Handle the three main statuses from API
      if (value === "active") return "active";
      if (value === "disabled") return "inactive";
      if (value === "pending") return "pending_approval";

      // Handle other known statuses
      if (["inactive", "pending_approval", "expired"].includes(value))
        return value;

      // Handle variants
      if (value === "pending-approval" || value === "pending approval")
        return "pending_approval";

      // Default to inactive for unknown values
      return "inactive";
    },
    isActive(status) {
      return this.normalizeStatus(status) === "active";
    },
    isInactive(status) {
      return this.normalizeStatus(status) === "inactive";
    },
    getStatusBadgeClass(status) {
      switch (this.normalizeStatus(status)) {
        case "active":
          return "badge-success";
        case "inactive":
          return "badge-secondary";
        case "pending_approval":
          return "badge-warning";
        case "expired":
          return "badge-danger";
        default:
          return "badge-secondary";
      }
    },

    getStatusDisplayName(status) {
      switch (this.normalizeStatus(status)) {
        case "active":
          return "Active";
        case "inactive":
          return "Inactive";
        case "pending_approval":
          return "Pending";
        case "expired":
          return "Expired";
        default:
          return status;
      }
    },
  },

  async mounted() {
    // Load initial data when component mounts
    await this.fetchAvailableCurrencies(); // Fetch currencies first
    await this.fetchSystemRates(); // Then fetch FX rates
  },

  // Watch for sort changes
  watch: {
    sortBy: {
      handler() {
        // TODO: If your API supports server-side sorting, add it here
        // For now, we'll rely on client-side sorting by b-table
        console.log("Sort by changed:", this.sortBy);
      },
    },
    sortDesc: {
      handler() {
        // TODO: If your API supports server-side sorting, add it here
        console.log("Sort direction changed:", this.sortDesc);
      },
    },
  },
};
</script>

<style scoped>
.nav-tabs .nav-link {
  color: #495057;
  border: 1px solid transparent;
  border-top-left-radius: 0.25rem;
  border-top-right-radius: 0.25rem;
}

.nav-tabs .nav-link.active {
  color: #495057;
  background-color: #fff;
  border-color: #dee2e6 #dee2e6 #fff;
}

.nav-tabs .nav-link:hover {
  border-color: #e9ecef #e9ecef #dee2e6;
}

.tab-content {
  border: 1px solid #dee2e6;
  border-top: none;
  padding: 20px;
}

.form-control-file {
  padding: 0.375rem 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
}

.table-responsive {
  max-height: 300px;
  overflow-y: auto;
}

.pagination-info {
  font-size: 14px;
}

/* Currency search indicator styles */
.text-info {
  color: #17a2b8 !important;
}

.action-top-search {
  position: relative;
}

.action-top-search small {
  font-size: 12px;
  line-height: 1.2;
}

/* Currency dropdown loading states */
.form-control:disabled {
  background-color: #e9ecef;
  opacity: 0.65;
  cursor: not-allowed;
}

.form-control:disabled option {
  color: #6c757d;
}

/* Rate History Chart styles */
.rate-history-chart-container {
  padding: 16px;
  background: #f9fafb;
  border-radius: 8px;
}

/* Old Rate column styles */
.text-success {
  color: #22c55e !important;
}

.text-danger {
  color: #ef4444 !important;
}

.font-italic {
  font-style: italic;
}
</style>
